import styled from "styled-components";
import cssObjectForPrimaryState from "./functions/cssObjectForPrimaryState";
import cssObjectForSecondaryState from "./functions/cssObjectForSecondaryState";
import merge from "lodash.merge";
import { StyledButtonProps } from "../Button.types";
import { Unit } from "../../../../types/globals";

const TRANSITION_DURATION = 300;
const TRANSITION_TIMING_FUNCTION = "ease-in-out";

const StyledButton = styled.button<StyledButtonProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: ${({ iconPosition }) =>
		iconPosition === "left" ? "row-reverse" : "row"};

	${({ theme, size, iconOnly }) => {
		switch (size) {
			case "small":
				return {
					gap: theme.space.xxs,
					padding: `0 ${iconOnly ? 0 : theme.space.md}`,
					width: iconOnly ? 32 : "auto",
					height: 32,
					borderRadius: theme.radii.md,
				};

			default:
				return {
					gap: theme.space.xs,
					padding: `0 ${iconOnly ? 0 : theme.space.lg}`,
					width: iconOnly ? 48 : "auto",
					height: 48,
					borderRadius: theme.radii.lg,
				};
		}
	}}

	&:not(:disabled) {
		cursor: pointer;
	}

	transition: background-color ${TRANSITION_DURATION}ms
			${TRANSITION_TIMING_FUNCTION},
		color ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
		border-color ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};

	.Typography {
		transition: color ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	}

	.Icon svg {
		transition: fill ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	}

	${({ iconOnly, theme, variant, ...props }) => {
		const palette = theme.palette;
		const COLOR_CTA_DEFAULT = palette.hexForRole("cta_default");
		const COLOR_CTA_HOVER = palette.hexForRole("cta_hover");
		const COLOR_CTA_ACTIVE = palette.hexForRole("cta_active");
		const COLOR_CTA_DISABLED = palette.hexForRole("cta_disabled");
		const COLOR_FG_LIGHT_1 = palette.hexFor("fg_light_1");
		const COLOR_BG_WHITE = palette.hexFor("bg_white");
		const COLOR_PRIMARY_ACCENT_100 = palette.hexFor("primary_accent_100");
		const COLOR_PRIMARY_ACCENT_300 = palette.hexFor("primary_accent_300");
		const COLOR_NEUTRAL_400 = palette.hexFor("neutral_400");
		const COLOR_NEUTRAL_500 = palette.hexFor("neutral_500");
		const COLOR_NEUTRAL_600 = palette.hexFor("neutral_600");

		switch (variant) {
			case "secondary":
				const BORDER_WIDTH: Unit = theme.borderWidths.sm;
				return props.colorOn === "bg_dark" ||
					props.colorOn === "bg_dark_variant"
					? merge(cssObjectForSecondaryState(COLOR_BG_WHITE, BORDER_WIDTH), {
							padding: `0 ${
								iconOnly ? 0 : `calc(${theme.space.lg} - ${BORDER_WIDTH})`
							}`,
							"&:hover": cssObjectForSecondaryState(
								COLOR_CTA_HOVER,
								BORDER_WIDTH
							),
							"&:active": cssObjectForSecondaryState(
								COLOR_CTA_ACTIVE,
								BORDER_WIDTH
							),
							"&:disabled": cssObjectForSecondaryState(
								COLOR_NEUTRAL_600,
								BORDER_WIDTH
							),
					  })
					: merge(cssObjectForSecondaryState(COLOR_CTA_DEFAULT, BORDER_WIDTH), {
							padding: `0 ${
								iconOnly ? 0 : `calc(${theme.space.lg} - ${BORDER_WIDTH})`
							}`,
							"&:hover": cssObjectForSecondaryState(
								COLOR_CTA_HOVER,
								BORDER_WIDTH
							),
							"&:active": cssObjectForSecondaryState(
								COLOR_CTA_ACTIVE,
								BORDER_WIDTH
							),
							"&:disabled": cssObjectForSecondaryState(
								COLOR_NEUTRAL_400,
								BORDER_WIDTH
							),
					  });
			default: {
				return props.colorOn === "bg_dark" ||
					props.colorOn === "bg_dark_variant"
					? merge(cssObjectForPrimaryState(COLOR_BG_WHITE, COLOR_CTA_DEFAULT), {
							"&:hover": cssObjectForPrimaryState(
								COLOR_PRIMARY_ACCENT_100,
								COLOR_CTA_HOVER
							),
							"&:active": cssObjectForPrimaryState(
								COLOR_PRIMARY_ACCENT_300,
								COLOR_CTA_ACTIVE
							),
							"&:disabled": cssObjectForPrimaryState(
								COLOR_NEUTRAL_500,
								COLOR_NEUTRAL_400
							),
					  })
					: merge(
							cssObjectForPrimaryState(COLOR_CTA_DEFAULT, COLOR_FG_LIGHT_1),
							{
								"&:hover": cssObjectForPrimaryState(
									COLOR_CTA_HOVER,
									COLOR_FG_LIGHT_1
								),
								"&:active": cssObjectForPrimaryState(
									COLOR_CTA_ACTIVE,
									COLOR_FG_LIGHT_1
								),
								"&:disabled": cssObjectForPrimaryState(
									COLOR_CTA_DISABLED,
									COLOR_NEUTRAL_400
								),
							}
					  );
			}
		}
	}};
`;

export { StyledButton };
